// The Memory rail's global-search orchestration hook. Network lifecycle lives here so the rail remains a
// render-only component. Blank input performs no request; invalid input fails closed locally; every valid
// request still crosses the authenticated API and is revalidated by the owning Memory module.

import { useEffect, useState } from 'react';
import { useSurfaceApi } from '../SurfaceApiContext.tsx';
import type { MemorySearchProjection, MemorySearchResultView } from '../know-projections.ts';
import { isValidMemorySearchQuery, normaliseMemorySearchQuery } from './memory-search.ts';

interface SearchSnapshot {
  readonly query: string;
  readonly data: MemorySearchProjection | null;
  readonly loading: boolean;
  readonly error: boolean;
}

/** Render-ready state for the global Memory search. */
export interface MemorySearchState {
  readonly active: boolean;
  readonly results: readonly MemorySearchResultView[];
  readonly loading: boolean;
  readonly error: 'invalid' | 'unavailable' | null;
}

const EMPTY_SNAPSHOT: SearchSnapshot = {
  query: '',
  data: null,
  loading: false,
  error: false,
};

/**
 * Search Memory for a valid nonblank query. Late responses are ignored, so fast typing can never replace a
 * newer result set with an older one. Blank input returns the ordinary-tree state without touching the API.
 */
export function useMemorySearch(input: string): MemorySearchState {
  const api = useSurfaceApi();
  const query = normaliseMemorySearchQuery(input);
  const [snapshot, setSnapshot] = useState<SearchSnapshot>(EMPTY_SNAPSHOT);

  useEffect(() => {
    if (!isValidMemorySearchQuery(query)) return;
    let live = true;
    setSnapshot({ query, data: null, loading: true, error: false });
    api
      .getMemorySearch(query)
      .then((data) => {
        if (live) setSnapshot({ query, data, loading: false, error: false });
      })
      .catch(() => {
        if (live) setSnapshot({ query, data: null, loading: false, error: true });
      });
    return () => {
      live = false;
    };
  }, [api, query]);

  if (query.length === 0) {
    return { active: false, results: [], loading: false, error: null };
  }
  if (!isValidMemorySearchQuery(query)) {
    return { active: true, results: [], loading: false, error: 'invalid' };
  }
  if (snapshot.query !== query || snapshot.loading) {
    return { active: true, results: [], loading: true, error: null };
  }
  return {
    active: true,
    results: snapshot.data?.results ?? [],
    loading: false,
    error: snapshot.error ? 'unavailable' : null,
  };
}
